import React, { useState, useEffect, useCallback } from 'react';
import FileDropZone from '../shared/FileDropZone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faUpload, faFileText, faRobot, faExclamationTriangle,
    faSpinner, faCheckCircle, faUniversity, faList, faCalculator, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import bankReconService from '../../services/bankReconService';
import BankStatementExtractionResults from './BankStatementExtractionResults';
import { allStatementLines } from './statementBlocks';
import { getErrorMessage } from '../../utils/errorMessages';
import './BankStatementCaptureModal.css';


const BankStatementCaptureModal = ({ isOpen, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [uploadState, setUploadState] = useState('idle');
    const [error, setError] = useState('');
    const [extractedData, setExtractedData] = useState(null);
    const [entitySuggestion, setEntitySuggestion] = useState(null);
    const [selectedLines, setSelectedLines] = useState([]);
    const [extractionWarnings, setExtractionWarnings] = useState([]);
    const [uploadId, setUploadId] = useState(null);
    const [reAnalysisGuidance, setReAnalysisGuidance] = useState('');
    const [extractionStep, setExtractionStep] = useState(0);
    const [pageProgress, setPageProgress] = useState({ current: 0, total: 0 });

    const extractionSteps = [
        { icon: faUpload,     label: 'Uploading statement',              detail: 'Securely transferring your document' },
        { icon: faFileText,   label: 'Reading document content',         detail: 'Extracting text and table data' },
        { icon: faUniversity, label: 'Identifying bank account details', detail: 'Detecting bank name, IBAN, and dates' },
        { icon: faList,       label: 'Parsing transactions',             detail: 'Extracting individual line items' },
        { icon: faCalculator, label: 'Classifying cash direction',       detail: 'Normalizing amounts as debits and credits' },
        { icon: faShieldAlt,  label: 'Validating extracted data',        detail: 'Structuring and verifying all entries' },
    ];

    const pollProgress = useCallback(async (id) => {
        try {
            const response = await bankReconService.getExtractionProgress(id);
            const { current_page, total_pages, is_complete } = response.data;
            setPageProgress({ current: current_page || 0, total: total_pages || 0 });
            return is_complete;
        } catch (err) {
            console.error('Progress polling error:', err);
            return false;
        }
    }, []);

    useEffect(() => {
        let progressInterval;
        if (uploadState === 'extracting' && uploadId) {
            progressInterval = setInterval(async () => {
                const isComplete = await pollProgress(uploadId);
                if (isComplete) clearInterval(progressInterval);
            }, 500);
        }
        return () => { if (progressInterval) clearInterval(progressInterval); };
    }, [uploadState, uploadId, pollProgress]);

    useEffect(() => {
        if (!uploadState || uploadState !== 'extracting') return;
        setExtractionStep(0);
        const interval = setInterval(() => {
            setExtractionStep(prev => {
                if (prev < extractionSteps.length - 1) return prev + 1;
                return prev;
            });
        }, 3500);
        return () => clearInterval(interval);
    }, [uploadState, extractionSteps.length]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setError('');
    };

    /* The drop zone itself is components/shared/FileDropZone. This is only
       what to do with the file once it arrives, so the file picker and the
       drop cannot end up disagreeing about it. */
    const acceptDroppedFile = (droppedFile) => {
        setFile(droppedFile);
        setError('');
    };

    const validateFile = (f) => {
        if (!f) return 'Please select a file';

        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'text/csv',
            // ISO 20022 camt.053 statements. Banks send these as .xml, .txt or
            // with no extension at all -- the server detects the format from the
            // file's CONTENT, so this list only has to stop obvious mistakes.
            'text/xml', 'application/xml', 'text/plain',
            'image/png', 'image/jpeg', 'image/jpg', 'image/tiff', 'image/tif',
            'image/bmp', 'image/webp',
        ];

        if (!allowedTypes.includes(f.type)) {
            const fileName = f.name.toLowerCase();
            const supportedExtensions = [
                '.pdf', '.doc', '.docx', '.xlsx', '.xls', '.csv',
                '.xml', '.txt',
                '.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.webp',
            ];
            const hasValidExtension = supportedExtensions.some(ext => fileName.endsWith(ext));
            if (!hasValidExtension) {
                return 'File type not supported. Please upload a PDF, Excel, CSV, Word document, bank CAMT file, or image.';
            }
        }

        const maxSize = 50 * 1024 * 1024;
        if (f.size > maxSize) return 'File size must be less than 50MB';
        return null;
    };

    const startExtraction = async (fileToUpload, guidance) => {
        setUploadState('extracting');
        setError('');

        try {
            const response = await bankReconService.uploadStatement(fileToUpload, guidance);

            if (response.data.status === 'processing' && response.data.upload_id) {
                setUploadId(response.data.upload_id);
                const currentUploadId = response.data.upload_id;
                let pollAttempts = 0;
                const maxPollAttempts = 300;

                const pollForCompletion = async () => {
                    try {
                        const progressResponse = await bankReconService.getExtractionProgress(currentUploadId);
                        const {
                            is_complete, status, extracted_data, error_message,
                            entity_suggestion, warnings: extractionWarnings,
                        } = progressResponse.data;

                        if (is_complete) {
                            if (status === 'failed') {
                                throw new Error(error_message || 'Analysis failed');
                            }

                            const validData = extracted_data || {};
                            setExtractedData(validData);
                            setEntitySuggestion(entity_suggestion || null);
                            setExtractionWarnings(extractionWarnings || []);
                            setUploadState('results');

                            // A CAMT file can hold several statement blocks, so
                            // the selection array spans every block's lines.
                            setSelectedLines(new Array(allStatementLines(validData).length).fill(true));
                            return;
                        }

                        pollAttempts++;
                        if (pollAttempts < maxPollAttempts) {
                            setTimeout(pollForCompletion, 1000);
                        } else {
                            throw new Error('Analysis timeout - processing took too long');
                        }
                    } catch (err) {
                        console.error('Polling error:', err);
                        setError(err.message || 'Failed to check analysis progress');
                        setUploadState('error');
                    }
                };

                setTimeout(pollForCompletion, 1000);
            }
        } catch (err) {
            console.error('Bank statement capture error:', err);
            if (err.response?.status === 400) {
                setError(getErrorMessage(err, 'Bank statement capture failed. Please check your file.'));
            } else if (err.response?.status === 413) {
                setError('File too large. Please upload a smaller file.');
            } else if (err.response?.status >= 500) {
                setError('Server error during statement capture. Please try again later.');
            } else {
                setError('Statement capture failed. Please check your connection and try again.');
            }
            setUploadState('error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const fileError = validateFile(file);
        if (fileError) { setError(fileError); return; }
        await startExtraction(file, reAnalysisGuidance || null);
    };

    const discardUpload = async (id) => {
        if (!id) return;
        try {
            await bankReconService.discardUpload(id);
        } catch (err) {
            console.error('Failed to discard upload:', err);
        }
    };

    const resetState = () => {
        setFile(null);
        setError('');
        setUploadState('idle');
        setExtractedData(null);
        setEntitySuggestion(null);
        setSelectedLines([]);
        setExtractionWarnings([]);
        setUploadId(null);
        setReAnalysisGuidance('');
        setPageProgress({ current: 0, total: 0 });
    };

    const handleClose = async () => {
        if (uploadId && (uploadState === 'results' || uploadState === 'error')) {
            await discardUpload(uploadId);
        }
        resetState();
        onClose();
    };

    const handleDiscard = async () => {
        if (uploadId) {
            await discardUpload(uploadId);
        }
        resetState();
        onClose();
    };

    const handleAccept = async (overrides) => {
        try {
            setError('');
            const response = await bankReconService.confirmStatement(uploadId, overrides);
            resetState();
            onSuccess(response.data);
        } catch (err) {
            console.error('Confirm statement failed:', err);
            setError(getErrorMessage(err, 'Failed to confirm statement. Please try again.'));
        }
    };

    const handleReAnalyze = async (guidance) => {
        if (!file) return;

        if (uploadId) {
            await discardUpload(uploadId);
            setUploadId(null);
        }

        setReAnalysisGuidance(guidance);
        await startExtraction(file, guidance);
    };

    const handleLineToggle = (idx) => {
        setSelectedLines(prev => {
            const updated = [...prev];
            updated[idx] = !updated[idx];
            return updated;
        });
    };

    const handleToggleAllLines = () => {
        const allSelected = selectedLines.every(Boolean);
        setSelectedLines(prev => prev.map(() => !allSelected));
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content contract-upload-modal bank-stmt-modal">
                <div className="modal-header">
                    <h2>
                        {uploadState === 'results' ? 'Bank Statement Extraction Results'
                            : 'Capture Bank Statement'}
                    </h2>
                    <button
                        className="close-button"
                        onClick={handleClose}
                        disabled={uploadState === 'extracting'}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {error && <div className="error-message">{error}</div>}

                {(uploadState === 'idle' || uploadState === 'error') && (
                    <form onSubmit={handleSubmit} className="upload-form">
                        <FileDropZone
                            className={`file-drop-zone ${file ? 'has-file' : ''}`}
                            activeClassName="active"
                            onFiles={(files) => acceptDroppedFile(files[0])}
                            onError={setError}
                        >
                            {file ? (
                                <div className="file-selected">
                                    <FontAwesomeIcon icon={faFileText} className="file-icon" />
                                    <div className="file-info">
                                        <span className="file-name">{file.name}</span>
                                        <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                    </div>
                                    <button type="button" className="remove-file" onClick={() => setFile(null)}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            ) : (
                                <div className="drop-zone-content">
                                    <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                                    <p className="drop-text">
                                        Drag and drop your bank statement here, or{' '}
                                        <label className="file-select-link">
                                            browse files
                                            <input
                                                type="file"
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.xml,.txt,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp"
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </p>
                                    <p className="file-requirements">
                                        Supported: PDF, Excel, CSV, Word, images (max 50MB)
                                    </p>
                                </div>
                            )}
                        </FileDropZone>

                        <div className="upload-info">
                            <h3>AI-Powered Statement Capture</h3>
                            <ul>
                                <li>AI will extract bank name, IBAN, account holder, and statement date</li>
                                <li>Individual transactions will be parsed with amounts, dates, and references</li>
                                <li>The system will auto-detect which entity the account belongs to</li>
                                <li>You can review and accept before anything is saved</li>
                            </ul>
                        </div>

                        <div className="modal-actions centered">
                            <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={!file}>
                                <FontAwesomeIcon icon={faRobot} /> Capture Statement
                            </button>
                        </div>
                    </form>
                )}

                {uploadState === 'extracting' && (
                    <div className="bank-stmt-processing-steps">
                        <div className="bank-stmt-ps-header">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Analysing your bank statement&hellip;</span>
                        </div>

                        {pageProgress.total > 0 && (
                            <div className="bank-stmt-page-progress">
                                Page {pageProgress.current} of {pageProgress.total}
                            </div>
                        )}

                        <ul className="bank-stmt-ps-steps">
                            {extractionSteps.map((step, i) => {
                                const done = extractionStep > i;
                                const active = extractionStep === i;
                                return (
                                    <li key={i} className={`bank-stmt-ps-step ${done ? 'done' : active ? 'active' : 'waiting'}`}>
                                        <span className="bank-stmt-ps-step-icon">
                                            {done
                                                ? <FontAwesomeIcon icon={faCheckCircle} />
                                                : active
                                                ? <FontAwesomeIcon icon={faSpinner} spin />
                                                : <span className="bank-stmt-ps-step-num">{i + 1}</span>
                                            }
                                        </span>
                                        <span className="bank-stmt-ps-step-text">
                                            <span className="bank-stmt-ps-step-label">{step.label}</span>
                                            {active && <span className="bank-stmt-ps-step-detail">{step.detail}</span>}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>

                        {file && file.size > 10 * 1024 * 1024 && (
                            <div className="bank-stmt-large-file-warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="bank-stmt-warning-icon" />
                                <span>
                                    Large file ({(file.size / 1024 / 1024).toFixed(1)}MB) -- processing may take up to 2 minutes
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {uploadState === 'results' && extractedData && (
                    <BankStatementExtractionResults
                        extractedData={extractedData}
                        entitySuggestion={entitySuggestion}
                        warnings={extractionWarnings}
                        selectedLines={selectedLines}
                        onLineToggle={handleLineToggle}
                        onToggleAllLines={handleToggleAllLines}
                        onAccept={handleAccept}
                        onDiscard={handleDiscard}
                        onReAnalyze={handleReAnalyze}
                        reAnalysisGuidance={reAnalysisGuidance}
                        setReAnalysisGuidance={setReAnalysisGuidance}
                    />
                )}
            </div>
        </div>
    );
};

export default BankStatementCaptureModal;
